/** @typedef {import('headlight-configurable-application').Pict} Pict */

const libPictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;
const ScopedEntityData = require('./_ScopedEntityData.js');

class CustomInputHandler extends libPictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {import('pict')} */
		this.pict;
		/** @type {import('pict') & { HeadlightPopover: any }} */
		this.fable;
		/** @type {any} */
		this.log;

		this.pict.MetaTemplate.addPattern('{~HideRecordSetControls:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpDataObject = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			let hideRecordSetControls = false;
			if (tmpDataObject && tmpDataObject?.hasOwnProperty('GroupExtra') && tmpDataObject.GroupExtra)
			{
				// if we find 'HideRecordSetControls' or 'Hide RecordSet Controls' in the GroupExtra string, hide the record set controls manually
				// NOTE: We also support 'Hide Table Controls' and 'HideTableControls' for backward compatibility with the TableControls provider since Table controls is the common name for both tables and record sets
				if ([ 'HideRecordSetControls', 'Hide RecordSet Controls', 'Hide Record Set Controls', 'HideTableControls', 'Hide Table Controls' ].some((i) => tmpDataObject.GroupExtra.includes(i)))
				{
					hideRecordSetControls = true;
				}
			}
			else if (tmpDataObject.MinimumRowCount && tmpDataObject.MaximumRowCount)
			{
				hideRecordSetControls = tmpDataObject.MinimumRowCount >= tmpDataObject.MaximumRowCount;
			}
			return (hideRecordSetControls) ? 'hide-recordset-controls' : '';
		});
	}

	/**
	 * Generates the HTML ID for a content display input element.
	 *
	 * @param {string} pInputHTMLID - The HTML ID of the input element.
	 * @return {string} - The generated HTML ID for the content display input element.
	 */
	getRecordSetHTMLID(pInputHTMLID)
	{
		return `#RECORDSET-FOR-${pInputHTMLID}`;
	}
	getRecordSetWrapperHTMLID(pInputHTMLID)
	{
		return `#RECORDSET-WRAPPER-FOR-${pInputHTMLID}`;
	}

	/**
	 * Tombstone the rows the row's selectors own before the row is spliced away.
	 *
	 * Row deletion is the one removal route with no input-level seam: `deleteDynamicTableRow` splices
	 * the record set and notifies nothing, so each selector in the row is asked for its owned rows here,
	 * the last point at which they still exist.
	 *
	 * @param {Object} pView - The dynamic-form view owning the row.
	 * @param {Record<string, any>} pGroup - The group descriptor (for its RecordSetAddress).
	 * @param {number|string} pRowIndex - The row about to be deleted.
	 * @return {number} - How many tombstones were staged.
	 */
	stageRowRemovalTombstones(pView, pGroup, pRowIndex)
	{
		if (!ScopedEntityData.isEnabled(this.pict))
		{
			return 0;
		}
		const tmpRemovals = ScopedEntityData.collectRowSelectorRemovals(this.pict, pView, pGroup, pRowIndex);
		if (tmpRemovals.length < 1)
		{
			return 0;
		}
		const tmpResult = ScopedEntityData.tombstoneRemovedRecords(this.pict, ScopedEntityData.DEFAULT_CONTEXT, tmpRemovals);
		if (tmpResult.Staged.length > 0)
		{
			this.pict.log.info(`Bulma-RecordSetControls: staged deletion of ${tmpResult.Staged.length} record(s) with row ${pRowIndex} of [${pGroup.RecordSetAddress}].`, { Records: tmpResult.Staged });
		}
		return tmpResult.Staged.length;
	}

	/**
	 * Handle a record set action from a dropdown menu.
	 * @param {string} pAction - The action to perform.
	 * @param {string} pViewHash - The hash of the view.
	 * @param {number} pGroupIndex - The group number of the table row.
	 * @param {string} pRowIndex - The key of the table row.
	 * @return {boolean} - True if the action was successful, false otherwise.
	 */
	handleRecordSetMenuButtonEvent(pAction, pViewHash, pGroupIndex, pRowIndex)
	{
		const tmpView = this.pict.views[pViewHash];
		const tmpGroup = tmpView.getGroup(pGroupIndex);
		const tmpDestinationObject = tmpView.sectionManifest.getValueByHash(tmpView.getMarshalDestinationObject(), tmpGroup.RecordSetAddress);

		// handle toast notifications for table row actions based on min/max row counts
		if (pAction === 'Delete')
		{
			if (tmpGroup.MinimumRowCount && (tmpDestinationObject.length <= tmpGroup.MinimumRowCount))
			{
				this.pict.log.error(`Cannot delete record set, minimum set count reached: ${tmpGroup.MinimumRowCount}`);
				this.pict.views.HeadlightPanels.toast({
					message: `Cannot delete record set, minimum set count reached: ${tmpGroup.MinimumRowCount}`,
					type: 'is-warning',
				});
				this.fable.HeadlightPopover.hideAll();
				return false;
			}
			this.stageRowRemovalTombstones(tmpView, tmpGroup, pRowIndex);
			this.pict.views[pViewHash].deleteDynamicTableRow(pGroupIndex, pRowIndex);
		}
		else if (pAction === 'MoveUp')
		{
			const tmpRowIndex = parseInt(String(pRowIndex), 10);
			if (tmpRowIndex == 0)
			{
				this.pict.log.error(`Cannot move record set up, already at the top: ${tmpRowIndex}`);
				this.pict.views.HeadlightPanels.toast({
					message: `Cannot move record set up, already at the top.`,
					type: 'is-warning',
				});
				this.fable.HeadlightPopover.hideAll();
				return false;
			}
			this.pict.views[pViewHash].moveDynamicTableRowUp(pGroupIndex, pRowIndex);
		}
		else if (pAction === 'MoveDown')
		{
			const tmpRowIndex = parseInt(String(pRowIndex), 10);
			if (tmpRowIndex == (tmpDestinationObject.length - 1))
			{
				this.pict.log.error(`Cannot move record set down, already at the bottom: ${tmpRowIndex}`);
				this.pict.views.HeadlightPanels.toast({
					message: `Cannot move record set down, already at the bottom.`,
					type: 'is-warning',
				});
				this.fable.HeadlightPopover.hideAll();
				return false;
			}
			this.pict.views[pViewHash].moveDynamicTableRowDown(pGroupIndex, pRowIndex);
		}
		else
		{
			this.pict.log.error(`Unknown action: ${pAction}`);
		}
		// Update any necessary controls
		this.prepareAnyExtraRecordSetControls();
		// Hide the popover menu after the action is performed
		this.fable.HeadlightPopover.hideAll();
		return true;
	}

	/**
	 * Enable/disable each record set's "Add new set" button(s) against MaximumRowCount.
	 *
	 * Previously selected `button[data-recordset-controls="add-set"]`, but that
	 * attribute sits on the wrapping <div>, not on the button -- so this matched
	 * nothing and RecordSet groups were never actually capped. Select on
	 * `data-max-sets` instead: only add buttons carry it, it picks up both the
	 * bottom bar and the opt-in top bar, and it will not catch the entry-mode
	 * toggle button that shares the same wrapper.
	 */
	prepareAnyExtraRecordSetControls()
	{
		const tmpEntryControls = this.getEntryControlsProvider();
		const tmpRecordSetWrappers = document.querySelectorAll('.section-recordset-wrapper');
		tmpRecordSetWrappers.forEach((pWrapper) =>
		{
			const tmpAddButtons = pWrapper.querySelectorAll('button[data-max-sets]');
			if (!tmpAddButtons.length || !tmpEntryControls)
			{
				return;
			}
			const tmpEntryCount = pWrapper.querySelectorAll('.RecordSetEntry').length;
			tmpEntryControls.applyAddButtonRowLimit(tmpAddButtons, tmpEntryCount, 'data-max-sets', 'data-min-sets');
		});
		// recalculate responsive UI adjustments for label alignments, etc.
		this.pict.providers.ResponsiveUIAdjustmentProvider.onWindowResize();
	}

	/**
	 * The shared entry-controls engine. Every knob (add-bar position, modal
	 * entry, entry point, the inline/modal toggle) is implemented once in
	 * Bulma-TableControls and driven from here for RecordSet groups -- including
	 * the three MetaTemplate macros, which are registered there and NEVER also
	 * here (a second addPattern on the same pattern silently shadows the first).
	 * @return {Object|null} - The Bulma-TableControls provider, or null.
	 */
	getEntryControlsProvider()
	{
		const tmpProvider = this.pict.providers['Bulma-TableControls'];
		if (!tmpProvider || (typeof tmpProvider.handleAddEntryButtonClickEvents !== 'function'))
		{
			this.pict.log.error('Bulma-RecordSetControls could not resolve the Bulma-TableControls entry-controls provider.');
			return null;
		}
		return tmpProvider;
	}

	/**
	 * The single entry point for both add bars on a RecordSet group. With no
	 * `EntryControls` on the group this reproduces exactly what the template's
	 * onclick used to inline: create the set, then the post-add fixups.
	 * @param {Event} pEvent - The click event.
	 * @param {string} pViewHash - The form view hash.
	 * @param {number|string} pGroupIndex - The group index.
	 * @return {boolean} - True when an entry was started.
	 */
	handleAddEntryButtonClickEvents(pEvent, pViewHash, pGroupIndex)
	{
		const tmpView = this.pict.views[pViewHash];
		if (!tmpView)
		{
			this.pict.log.error(`Entry controls could not resolve view [${pViewHash}].`);
			return false;
		}
		const tmpEntryControls = this.getEntryControlsProvider();
		const tmpGroup = tmpView.getGroup(pGroupIndex);
		if (!tmpEntryControls || !tmpEntryControls.getEntryControlsConfig(tmpGroup))
		{
			// Legacy path.
			tmpView.createDynamicTableRow(pGroupIndex);
			this.handleAddRowButtonClickEvents(pEvent);
			return true;
		}
		return tmpEntryControls.handleAddEntryButtonClickEvents(pEvent, pViewHash, pGroupIndex);
	}

	/**
	 * Flip a RecordSet group between inline and modal entry.
	 * @param {string} pViewHash - The form view hash.
	 * @param {number|string} pGroupIndex - The group index.
	 * @return {boolean} - True on success.
	 */
	toggleEntryMode(pViewHash, pGroupIndex)
	{
		const tmpEntryControls = this.getEntryControlsProvider();
		if (!tmpEntryControls)
		{
			return false;
		}
		return tmpEntryControls.toggleEntryMode(pViewHash, pGroupIndex);
	}

	/**
	 * Handle the click event for the "Add Row" button in a set.
	 * NOTE: This is a secondary call to prepare any extra set controls after the row is added/rendered - it doesn't actually add the row (that is handled by the Pict Provider).
	 * @param {Event} event - The click event.
	 */
	handleAddRowButtonClickEvents(event)
	{
		setTimeout(() =>
		{
			// Update any necessary controls
			this.prepareAnyExtraRecordSetControls();
		}, 100);
	}
}

module.exports = CustomInputHandler;
